import ApplicationLogo from '@/components/ui/ApplicationLogo';
import Link from 'next/link';
import Image from 'next/image';
import React, { Fragment } from 'react';
import LinkFooter from './links/LinkFooter';
import TitleLinksFooter from './titles/TitleLinksFooter';
import { format } from 'date-fns';
import { Routes } from '@/settings/routes';
import { FaCcMastercard, FaCcPaypal, FaCcVisa, FaFacebookF, FaInstagram, FaLinkedin, FaPinterest, FaWhatsapp, FaXTwitter } from 'react-icons/fa6';
import { PiMoney, PiMoneyWavy } from 'react-icons/pi';


import { DynamicEventsFooter, DynamicLuxuryFooter, DynamicPackagesFooter, DynamicToursFooter, DynamicTravelGuidesFooter } from '../ui/DynamicLinksFooter';
import { App_text } from '@/settings/AppText';
import SpeedDial from '../ui/SpeedDial';
import { FiPhoneCall, FiMail, FiMapPin } from 'react-icons/fi';
import ButtonTailorMade from '../ui/ButtonTailorMade';
import { BsBank2 } from 'react-icons/bs';

const ensureProtocol = (value = '', protocol) => {
    if (!value) return '#';
    return value.startsWith(`${protocol}:`) ? value : `${protocol}:${value}`;
};

const stripContactProtocol = (value = '') => value.replace(/^(tel:|mailto:)/, '');

const getWhatsAppHref = (value = '') => {
    if (!value) return '#';
    if (value.startsWith('http')) return value;
    return `https://api.whatsapp.com/send?phone=${value}`;
};

export default function Footer() {
    const phoneHref = ensureProtocol(Routes.FRONT_phone, 'tel');
    const emailHref = ensureProtocol(Routes.FRONT_email, 'mailto');
    const whatsappHref = getWhatsAppHref(Routes.FRONT_whatsapp);
    const phoneText = stripContactProtocol(Routes.FRONT_phone);
    const emailText = stripContactProtocol(Routes.FRONT_email);

    return (
        <Fragment>
            <ButtonTailorMade />
            <footer className="relative bg-white">
                <div className="relative z-10 px-4 pt-12 pb-8 mx-auto w-full max-w-8xl lg:px-6 lg:pt-16">
                    {/* ── Main Footer Content ── */}
                    <div className="grid grid-cols-1 gap-10 mb-12 lg:mb-16 lg:grid-cols-[2fr_3fr]">
                        <div>
                            {/* Brand */}
                            <div className="flex flex-col gap-5">
                                <div className="mb-2">
                                    <ApplicationLogo />
                                </div>
                                <p className="max-w-sm text-sm leading-relaxed text-gray-600">
                                    Discover Morocco's magic with expertly crafted tours, unforgettable events, and personalized travel experiences.
                                </p>
                                <div className="flex flex-col gap-3 mt-2">
                                    <span className="text-sm font-medium text-gray-700">Connect With Us</span>
                                    <ul className="flex gap-2.5 items-center">
                                        <li>
                                            <Link
                                                prefetch={false}
                                                href={Routes.FRONT_instagram}
                                                target="_blank"
                                                aria-label="Instagram"
                                                className="flex justify-center items-center w-10 h-10 text-gray-600 bg-gray-100 rounded-lg transition-all hover:bg-custom-orange-600 hover:text-custom-orange-800 hover:scale-110"
                                            >
                                                <FaInstagram className="w-5 h-5" />
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                prefetch={false}
                                                href={Routes.FRONT_facebook}
                                                target="_blank"
                                                aria-label="Facebook"
                                                className="flex justify-center items-center w-10 h-10 text-gray-600 bg-gray-100 rounded-lg transition-all hover:bg-custom-orange-600 hover:text-custom-orange-800 hover:scale-110"
                                            >
                                                <FaFacebookF className="w-5 h-5" />
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                prefetch={false}
                                                href={Routes.FRONT_twitter}
                                                target="_blank"
                                                aria-label="Twitter"
                                                className="flex justify-center items-center w-10 h-10 text-gray-600 bg-gray-100 rounded-lg transition-all hover:bg-custom-orange-600 hover:text-custom-orange-800 hover:scale-110"
                                            >
                                                <FaXTwitter className="w-5 h-5" />
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                prefetch={false}
                                                href={Routes.FRONT_Linkedin}
                                                target="_blank"
                                                aria-label="LinkedIn"
                                                className="flex justify-center items-center w-10 h-10 text-gray-600 bg-gray-100 rounded-lg transition-all hover:bg-custom-orange-600 hover:text-custom-orange-800 hover:scale-110"
                                            >
                                                <FaLinkedin className="w-5 h-5" />
                                            </Link>
                                        </li>
                                        <li>
                                            <Link
                                                prefetch={false}
                                                href={Routes.FRONT_Pinterest}
                                                target="_blank"
                                                aria-label="Pinterest"
                                                className="flex justify-center items-center w-10 h-10 text-gray-600 bg-gray-100 rounded-lg transition-all hover:bg-custom-orange-600 hover:text-custom-orange-800 hover:scale-110"
                                            >
                                                <FaPinterest className="w-5 h-5" />
                                            </Link>
                                        </li>
                                    </ul>
                                </div>
                                <div className="flex flex-wrap gap-2 items-center mt-2 lg:hidden">
                                    <Image src="/media/footer/1.jpg" alt="Accepted payment methods" width={60} height={38} className="object-contain h-auto" />
                                    <Image src="/media/footer/2.png" alt="Trust badge" width={60} height={38} className="object-contain h-auto" />
                                    <Image src="/media/footer/3.png" alt="Certification badge" width={60} height={38} className="object-contain h-auto" />
                                    <Image src="/media/footer/4.png" alt="Security badge" width={60} height={38} className="object-contain h-auto" />
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-1 gap-8">
                            {/* ROW 1: Quick Links + tour groups */}
                            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-4">
                                {/* Quick Links */}
                                <div className="flex flex-col gap-4">
                                    <TitleLinksFooter>{App_text.footer.links}</TitleLinksFooter>
                                    <ul className="flex flex-col gap-2.5">
                                        <li>
                                            <LinkFooter href={Routes.FRONT_about_us}>About Us</LinkFooter>
                                        </li>
                                        <li>
                                            <LinkFooter href={Routes.FRONT_customer_reviews}>Customer Reviews</LinkFooter>
                                        </li>
                                        <li>
                                            <LinkFooter href={Routes.FRONT_faqs}>FAQs</LinkFooter>
                                        </li>
                                        <li>
                                            <LinkFooter href={Routes.FRONT_tailor_made}>Create Your Trip</LinkFooter>
                                        </li>
                                        <li>
                                            <LinkFooter href={Routes.FRONT_contact}>Contact Us</LinkFooter>
                                        </li>
                                        <li>
                                            <LinkFooter href={Routes.FRONT_terms_of_sale}>Terms of Sale</LinkFooter>
                                        </li>
                                        <li>
                                            <LinkFooter href={Routes.FRONT_privacy_policy}>Privacy Policy</LinkFooter>
                                        </li>
                                    </ul>
                                </div>

                                <DynamicToursFooter start={0} end={1} title="Region" titleClickable={false} />
                                <DynamicToursFooter start={1} end={2} title="Durations" titleClickable={false} />
                                <DynamicToursFooter start={2} end={3} title="Styles" titleClickable={false} />
                            </div>

                            {/* Divider */}
                            <div className="border-t border-gray-100" />

                            {/* ROW 2: service categories */}
                            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-4">
                                <DynamicPackagesFooter />
                                <DynamicEventsFooter />
                                <DynamicLuxuryFooter />
                                <DynamicTravelGuidesFooter />
                            </div>
                        </div>
                    </div>

                    {/* ── Contact Info Bar ── */}
                    <div className="grid grid-cols-1 gap-4 py-8 mb-8 border-t border-gray-200 md:grid-cols-3 lg:gap-6">
                        <Link prefetch={false} href={phoneHref} className="flex gap-3 items-center p-4 bg-gray-50 rounded-lg transition-colors hover:bg-custom-orange-50 group">
                            <div className="flex justify-center items-center w-12 h-12 bg-white rounded-lg shadow-sm transition-colors text-custom-orange-600 group-hover:bg-custom-orange-600 group-hover:text-custom-orange-800">
                                <FiPhoneCall className="w-5 h-5" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs font-medium text-gray-500">Call us</span>
                                <span className="text-sm font-semibold text-gray-900">{App_text.pages.contact_us.phone || phoneText}</span>
                            </div>
                        </Link>

                        <Link
                            prefetch={false}
                            href={whatsappHref}
                            target="_blank"
                            className="flex gap-3 items-center p-4 bg-gray-50 rounded-lg transition-colors hover:bg-custom-orange-50 group"
                        >
                            <div className="flex justify-center items-center w-12 h-12 bg-white rounded-lg shadow-sm transition-colors text-custom-orange-600 group-hover:bg-custom-orange-600 group-hover:text-custom-orange-800">
                                <FaWhatsapp className="w-5 h-5" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs font-medium text-gray-500">WhatsApp</span>
                                <span className="text-sm font-semibold text-gray-900">{App_text.pages.contact_us.whatsapp}</span>
                            </div>
                        </Link>

                        <Link prefetch={false} href={emailHref} className="flex gap-3 items-center p-4 bg-gray-50 rounded-lg transition-colors hover:bg-custom-orange-50 group">
                            <div className="flex justify-center items-center w-12 h-12 bg-white rounded-lg shadow-sm transition-colors text-custom-orange-600 group-hover:bg-custom-orange-600 group-hover:text-custom-orange-800">
                                <FiMail className="w-5 h-5" />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-xs font-medium text-gray-500">Email us</span>
                                <span className="text-sm font-semibold text-gray-900 break-all">{emailText}</span>
                            </div>
                        </Link>
                    </div>

                    {/* ── Bottom Bar ── */}
                    <div className="flex flex-col gap-4 justify-between items-center pt-6 border-t border-gray-200 md:flex-row">
                        <p className="text-sm text-gray-600">
                            © {format(new Date(), 'yyyy')}{' '}
                            <Link prefetch={false} href={'/'} className="font-medium transition-colors text-custom-orange-600 hover:text-custom-orange-700">
                                {App_text.APP_NAME}
                            </Link>
                            . All rights reserved
                        </p>

                        {/* Payment icons */}
                    <div className="flex gap-3 items-center flex-wrap">
                            <FaCcVisa className="w-8 h-8 text-[#1A1F71]" />
                            <FaCcMastercard className="w-8 h-8 text-[#EB001B]" />
                            <FaCcPaypal className="w-8 h-8 text-[#003087]" />
                            <BsBank2 className="w-5 h-5 text-gray-500" />
                            <PiMoney className="w-5 h-5 text-gray-500" />
                        </div>
                    </div>
                </div>
            </footer>
            <SpeedDial />
        </Fragment>
    );
}
